/**
 * ScoreSettings.jsx — Gestão dos ponderadores e benchmarks do Score de Saúde.
 * Validação: soma dos pesos tem de ser sempre 100%.
 */

import React, { useEffect, useState } from "react";
import { fetchConfiguracoes, updateConfiguracao, updateScorePesos } from "../../services/api";

const PESOS = [
  { chave: "score_peso_risco",          label: "% Tarefas em Risco" },
  { chave: "score_peso_aprov_externa",  label: "Tempo em Aprovação Externa" },
  { chave: "score_peso_aguardo_info",   label: "Tempo em Aguardo Informações" },
  { chave: "score_peso_ritmo",          label: "Ritmo de Conclusão" },
];

const BENCHMARKS = [
  { chave: "score_benchmark_aprov_externa", label: "Aprovação Externa saudável (dias)" },
  { chave: "score_benchmark_aguardo_info",  label: "Aguardo Informações saudável (dias)" },
];

export default function ScoreSettings() {
  const [valores, setValores] = useState({});
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    fetchConfiguracoes()
      .then((configs) => {
        const mapa = {};
        configs.forEach((c) => { mapa[c.chave] = c.valor; });
        setValores(mapa);
      })
      .finally(() => setLoading(false));
  }, []);

  const somaPesos = PESOS.reduce((acc, p) => acc + Number(valores[p.chave] || 0), 0);
  const somaValida = Math.abs(somaPesos - 100) < 0.01;

  const guardarTudo = async () => {
    if (!somaValida) return;
    setSalvando(true);
    setFeedback(null);
    try {
      await updateScorePesos({
        score_peso_risco:         Number(valores["score_peso_risco"]),
        score_peso_aprov_externa: Number(valores["score_peso_aprov_externa"]),
        score_peso_aguardo_info:  Number(valores["score_peso_aguardo_info"]),
        score_peso_ritmo:         Number(valores["score_peso_ritmo"]),
      });
      await Promise.all(BENCHMARKS.map((c) => updateConfiguracao(c.chave, valores[c.chave])));
      setFeedback({ tipo: "ok", msg: "Todas as configurações guardadas com sucesso." });
    } catch (e) {
      setFeedback({ tipo: "erro", msg: e.message });
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <p style={styles.loading}>A carregar…</p>;

  return (
    <div>
      {/* Pesos */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitulo}>Ponderadores (soma deve ser 100%)</h3>
        <div style={styles.pesosGrid}>
          {PESOS.map(({ chave, label }) => (
            <div key={chave} style={styles.pesoCard}>
              <label style={styles.label}>{label}</label>
              <div style={styles.inputRow}>
                <input
                  style={styles.input}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={valores[chave] ?? ""}
                  onChange={(e) =>
                    setValores((prev) => ({ ...prev, [chave]: e.target.value }))
                  }
                />
                <span style={styles.unidade}>%</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ ...styles.somaBar, color: somaValida ? "#16a34a" : "#dc2626" }}>
          Total: {somaPesos.toFixed(1)}%
          {!somaValida && " — A soma tem de ser exactamente 100%"}
        </div>
      </div>

      {/* Benchmarks */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitulo}>Benchmarks de referência</h3>
        <p style={styles.descricao}>
          Número de dias considerado saudável. Acima deste valor, o score penaliza progressivamente.
        </p>
        <div style={styles.benchGrid}>
          {BENCHMARKS.map(({ chave, label }) => (
            <div key={chave} style={styles.pesoCard}>
              <label style={styles.label}>{label}</label>
              <div style={styles.inputRow}>
                <input
                  style={styles.input}
                  type="number"
                  min="1"
                  max="30"
                  value={valores[chave] ?? ""}
                  onChange={(e) =>
                    setValores((prev) => ({ ...prev, [chave]: e.target.value }))
                  }
                />
                <span style={styles.unidade}>dias</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        style={{ ...styles.btnGuardar, opacity: !somaValida || salvando ? 0.5 : 1 }}
        onClick={guardarTudo}
        disabled={!somaValida || salvando}
      >
        {salvando ? "A guardar…" : "Guardar Tudo"}
      </button>

      {feedback && (
        <p style={feedback.tipo === "ok" ? styles.ok : styles.erro}>{feedback.msg}</p>
      )}
    </div>
  );
}

const styles = {
  section: { background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 1px 3px rgba(0,0,0,.08)", marginBottom: 20 },
  sectionTitulo: { fontSize: 15, fontWeight: 600, margin: "0 0 16px", color: "#1e293b" },
  descricao: { fontSize: 13, color: "#64748b", margin: "0 0 16px" },
  pesosGrid: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 },
  benchGrid: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 },
  pesoCard: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151" },
  inputRow: { display: "flex", alignItems: "center", gap: 8 },
  input: { width: 72, padding: "8px 10px", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 16, fontWeight: 700, textAlign: "center" },
  unidade: { fontSize: 14, color: "#64748b" },
  somaBar: { marginTop: 16, fontSize: 14, fontWeight: 600 },
  btnGuardar: { padding: "12px 32px", backgroundColor: "#6366f1", color: "#fff", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer", fontWeight: 700 },
  ok: { fontSize: 13, color: "#16a34a", marginTop: 12 },
  erro: { fontSize: 13, color: "#dc2626", marginTop: 12 },
  loading: { color: "#64748b" },
};
